import { API_BASE_URL } from './config';
import type { UserProfileData } from '@contexts/AuthContextStore';

/**
 * Wire shape returned by `GET /api/v1/me/profile` and `PUT /api/v1/me/profile`.
 *
 * `data` is opaque to the wire — see {@link UserProfileData} for the
 * frontend-owned shape.
 */
export interface UserProfileResponse {
  data: UserProfileData;
  completedAt: string | null;
  skipped: boolean;
  hasCompleted: boolean;
}

function authHeaders(token: string): HeadersInit {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
}

function extractMessage(body: unknown, fallback: string): string {
  if (
    body &&
    typeof body === 'object' &&
    'message' in body &&
    typeof (body as { message: unknown }).message === 'string'
  ) {
    return (body as { message: string }).message;
  }
  return fallback;
}

async function parseJsonOrThrow(
  response: Response,
  fallback: string,
): Promise<UserProfileResponse> {
  if (!response.ok) {
    // Best-effort error body parse — server might have returned text/HTML.
    const errorBody = await response.json().catch(() => null);
    throw new Error(extractMessage(errorBody, fallback));
  }
  // 2xx with an unparseable body is a server bug; surface it instead of
  // returning a silently-empty profile snapshot.
  try {
    return (await response.json()) as UserProfileResponse;
  } catch {
    throw new Error(fallback);
  }
}

/** Fetch the current user's onboarding profile. */
export async function getUserProfile(token: string): Promise<UserProfileResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/me/profile`, {
    method: 'GET',
    headers: authHeaders(token),
  });
  return parseJsonOrThrow(response, 'Failed to fetch profile');
}

/**
 * Upsert the current user's onboarding profile. Pass `skipped=true` when the
 * user pressed "Skip for now" — `data` should be empty in that case.
 */
export async function saveUserProfile(
  token: string,
  data: UserProfileData,
  skipped: boolean,
): Promise<UserProfileResponse> {
  const response = await fetch(`${API_BASE_URL}/api/v1/me/profile`, {
    method: 'PUT',
    headers: authHeaders(token),
    body: JSON.stringify({ data, skipped }),
  });
  return parseJsonOrThrow(response, 'Failed to save profile');
}
